import { useEffect } from "react";
import { Switch, Route, Router as WouterRouter } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import NotFound from "@/pages/not-found";


import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ProviderRegister from "@/pages/auth/ProviderRegister";
import MemberRegister from "@/pages/auth/MemberRegister";
import AgentRegister from "@/pages/auth/AgentRegister";
import MemberDashboard from "@/pages/member/Dashboard";
import ProviderSearch from "@/pages/member/ProviderSearch";
import ProviderDetail from "@/pages/member/ProviderDetail";
import MyEstimates from "@/pages/member/MyEstimates";
import CareTeam from "@/pages/member/CareTeam";
import CostEstimates from "@/pages/member/CostEstimates";
import SavedEstimates from "@/pages/member/SavedEstimates";
import SavedBundles from "@/pages/member/SavedBundles";
import MembershipCard from "@/pages/member/MembershipCard";
import MemberHousehold from "@/pages/member/Household";
import MemberSettings from "@/pages/member/Settings";
import MemberNotifications from "@/pages/member/Notifications";
import ProviderNotifications from "@/pages/provider/Notifications";
import AgentNotifications from "@/pages/agent/Notifications";
import MemberAgents from "@/pages/member/Agents";
import MemberReports from "@/pages/member/Reports";
import { MyAppointments } from "@/pages/member/MyAppointments";
import ManageAppointments from "@/pages/provider/ManageAppointments";
import ProviderDashboard from "@/pages/provider/Dashboard";
import ProviderReports from "@/pages/provider/Reports";
import Patients from "@/pages/provider/Patients";
import CptCodes from "@/pages/provider/CptCodes";
import Bundles from "@/pages/provider/Bundles";
import CreateBundlePage from "@/pages/provider/CreateBundlePage";
import CostEstimator from "@/pages/provider/CostEstimator";
import ProviderSavedEstimates from "@/pages/provider/SavedEstimates";
import CheckMember from "@/pages/provider/CheckMember";
import ProviderSettings from "@/pages/provider/Settings";
import ProviderAgents from "@/pages/provider/Agents";
import AddProvider from "@/pages/provider/AddProvider";
import GroupTeam from "@/pages/provider/GroupTeam";
import AgentDashboard from "@/pages/agent/Dashboard";
import AgentMembers from "@/pages/agent/Members";
import AgentProviders from "@/pages/agent/Providers";
import AgentCostEstimator from "@/pages/agent/CostEstimator";
import AgentSettings from "@/pages/agent/Settings";
import AddAgent from "@/pages/agent/AddAgent";
import PublicProviderProfile from "@/pages/public/ProviderProfile";
import ReviewPage from "@/pages/public/ReviewPage";
import ProviderReviews from "@/pages/provider/Reviews";
import BundleDetail from "@/pages/public/BundleDetail";

// Suppress harmless ResizeObserver errors that crash the Webpack Dev Server overlay
const _showError = window.console.error;
window.console.error = (...args: any[]) => {
  if (typeof args[0] === 'string' && args[0].includes('ResizeObserver loop')) {
    return;
  }
  _showError(...args);
};

window.addEventListener('error', e => {
  if (e.message && (e.message.includes('ResizeObserver loop') || e.message.includes('undelivered notifications'))) {
    e.stopImmediatePropagation();
    return;
  }
  console.error('[GlobalError]', e.error || e.message);
  const debugEl = document.getElementById('global-debug-error');
  if (debugEl) {
    debugEl.innerText = `[Unhandled Error] ${e.message}\n${e.error?.stack || ''}`;
    debugEl.style.display = 'block';
  }
});

window.addEventListener('unhandledrejection', e => {
  console.error('[UnhandledRejection]', e.reason);
  const debugEl = document.getElementById('global-debug-error');
  if (debugEl) {
    debugEl.innerText = `[Unhandled Rejection] ${e.reason?.message || e.reason}\n${e.reason?.stack || ''}`;
    debugEl.style.display = 'block';
  }
});

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30000 } }
});

function Router() {
  return (
    <Switch>
      <Route path="/" component={Landing} />
      <Route path="/login" component={Login} />
      <Route path="/register" component={Register} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/provider/register" component={ProviderRegister} />
      <Route path="/member/register" component={MemberRegister} />
      <Route path="/agent/register" component={AgentRegister} />
      <Route path="/member/dashboard" component={MemberDashboard} />
      <Route path="/member/search" component={ProviderSearch} />
      <Route path="/member/providers/:name/:id" component={ProviderDetail} />
      <Route path="/member/providers/:id" component={ProviderDetail} />
      <Route path="/member/care-team" component={CareTeam} />
      <Route path="/member/cost-estimates" component={CostEstimates} />
      <Route path="/member/saved-estimates" component={SavedEstimates} />
      <Route path="/member/saved-bundles" component={SavedBundles} />
      <Route path="/member/bundles" component={SavedBundles} />
      <Route path="/member/estimates" component={SavedEstimates} />
      <Route path="/member/membership-card" component={MembershipCard} />
      <Route path="/member/household" component={MemberHousehold} />
      <Route path="/member/family" component={MemberHousehold} />
      <Route path="/member/settings" component={MemberSettings} />
      <Route path="/member/agents" component={MemberAgents} />
      <Route path="/member/notifications" component={MemberNotifications} />
      <Route path="/member/appointments" component={MyAppointments} />
      <Route path="/member/reports" component={MemberReports} />
      <Route path="/provider/dashboard" component={ProviderDashboard} />
      <Route path="/provider/reports" component={ProviderReports} />
      <Route path="/provider/appointments" component={ManageAppointments} />
      <Route path="/provider/patients" component={Patients} />
      <Route path="/provider/cpt-codes" component={CptCodes} />
      <Route path="/provider/cpt-codes/add" component={CptCodes} />
      <Route path="/provider/bundles" component={Bundles} />
      <Route path="/provider/bundles/create" component={CreateBundlePage} />
      <Route path="/provider/bundles/edit/:id" component={CreateBundlePage} />
      <Route path="/provider/cost-estimator" component={CostEstimator} />
      <Route path="/provider/saved-estimates" component={ProviderSavedEstimates} />
      <Route path="/provider/check-member" component={CheckMember} />
      <Route path="/provider/notifications" component={ProviderNotifications} />
      <Route path="/provider/settings" component={ProviderSettings} />
      <Route path="/provider/agents" component={ProviderAgents} />
      <Route path="/provider/add-provider" component={AddProvider} />
      <Route path="/provider/team" component={GroupTeam} />
      <Route path="/agent/dashboard" component={AgentDashboard} />
      <Route path="/agent/members" component={AgentMembers} />
      <Route path="/agent/providers" component={AgentProviders} />
      <Route path="/agent/notifications" component={AgentNotifications} />
      <Route path="/agent/cost-estimator" component={AgentCostEstimator} />
      <Route path="/agent/settings" component={AgentSettings} />
      <Route path="/agent/add-agent" component={AddAgent} />
      <Route path="/provider/reviews" component={ProviderReviews} />
      <Route path="/review/:token" component={ReviewPage} />
      <Route path="/bundle/:id" component={BundleDetail} />
      <Route path="/bundles/:id" component={BundleDetail} />
      <Route path="/provider/:providerId/bundle/:bundleId" component={BundleDetail} />
      <Route path="/provider/:name/:id" component={ProviderDetail} />
      <Route path="/provider/:id" component={ProviderDetail} />
      <Route path="/providers/:name/:id" component={ProviderDetail} />
      <Route path="/providers/:id" component={ProviderDetail} />
      <Route component={NotFound} />
    </Switch>
  );
}

const initImpersonationSync = () => {
  if (typeof window === "undefined") return;
  try {
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token") || searchParams.get("impersonateToken");
    const impersonatedUserStr = searchParams.get("impersonatedUser");

    console.log("[ImpersonationSync] Checking URL params - Token:", token ? "FOUND" : "NONE", "UserPayload:", impersonatedUserStr ? "FOUND" : "NONE");

    // 1. Check window.name payload if present
    if (window.name && window.name.startsWith("{")) {
      try {
        const payload = JSON.parse(window.name);
        if (payload.token && payload.user) {
          console.log("[ImpersonationSync] Found payload in window.name:", payload.user);
          const currentToken = localStorage.getItem("token");
          if (currentToken && currentToken !== payload.token && !payload.adminToken) {
            localStorage.setItem("adminToken", currentToken);
          } else if (payload.adminToken) {
            localStorage.setItem("adminToken", payload.adminToken);
          }

          localStorage.setItem("token", payload.token);
          payload.user.isImpersonating = true;
          localStorage.setItem("user", JSON.stringify(payload.user));
          localStorage.setItem("isImpersonating", "true");
          window.name = "";
          return;
        }
      } catch (e) {
        console.error("[ImpersonationSync] Error parsing window.name:", e);
      }
    }

    // 2. Check URL ?token= parameter
    if (token) {
      const currentToken = localStorage.getItem("token");
      if (currentToken && currentToken !== token) {
        console.log("[ImpersonationSync] Preserving admin token before switching:", currentToken ? "SAVED" : "NONE");
        localStorage.setItem("adminToken", currentToken);
      }

      localStorage.setItem("token", token);
      localStorage.setItem("isImpersonating", "true");

      if (impersonatedUserStr) {
        try {
          const userObj = JSON.parse(impersonatedUserStr);
          userObj.isImpersonating = true;
          if (userObj.role === 'PROVIDER' || userObj.role === 'CLINIC') {
            userObj.provider = userObj.provider || { status: 'APPROVED' };
            if (userObj.provider && !userObj.provider.status) userObj.provider.status = 'APPROVED';
          } else if (userObj.role === 'MEMBER') {
            userObj.member = userObj.member || { status: 'ACTIVE' };
            if (userObj.member && !userObj.member.status) userObj.member.status = 'ACTIVE';
          } else if (userObj.role === 'AGENT') {
            userObj.agent = userObj.agent || { status: 'APPROVED' };
            if (userObj.agent && !userObj.agent.status) userObj.agent.status = 'APPROVED';
          }
          console.log("[ImpersonationSync] Parsed impersonatedUser successfully:", userObj);
          localStorage.setItem("user", JSON.stringify(userObj));
        } catch (e) {
          console.error("[ImpersonationSync] Error parsing impersonatedUser string:", e);
        }
      } else {
        // Decode JWT token payload synchronously to set user role immediately so layouts do not redirect to login
        try {
          const base64Url = token.split(".")[1];
          if (base64Url) {
            const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            const jsonPayload = decodeURIComponent(
              atob(base64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
            );
            const jwtPayload = JSON.parse(jsonPayload);
            console.log("[ImpersonationSync] Decoded JWT payload:", jwtPayload);
            if (jwtPayload.id && jwtPayload.role) {
              const minimalUser = {
                id: jwtPayload.id,
                email: jwtPayload.email,
                role: jwtPayload.role,
                isImpersonating: true,
                provider: jwtPayload.role === "PROVIDER" || jwtPayload.role === "CLINIC" ? { status: "APPROVED" } : null,
                member: jwtPayload.role === "MEMBER" ? { status: "ACTIVE" } : null,
                agent: jwtPayload.role === "AGENT" ? { status: "APPROVED" } : null,
              };
              localStorage.setItem("user", JSON.stringify(minimalUser));
            }
          }
        } catch (e) {
          console.error("[ImpersonationSync] Error decoding JWT:", e);
        }
      }

      // Clean query parameters from address bar cleanly
      searchParams.delete("token");
      searchParams.delete("impersonateToken");
      searchParams.delete("impersonatedUser");
      const newSearch = searchParams.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "") + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
      console.log("[ImpersonationSync] Cleaned URL query params. Current pathname:", window.location.pathname);
    }
  } catch (e) {
    console.error("[ImpersonationSync] Fatal sync error:", e);
  }
};

// Run synchronously before React mounts components
initImpersonationSync();

function App() {
  useEffect(() => {
    if (typeof window !== "undefined") {
      const p = window.location.pathname;
      if (p.startsWith("/admin")) {
        const host = window.location.hostname;
        const currentPort = window.location.port;
        const isLocal = host === "localhost" || host === "127.0.0.1" || /^(?:\d{1,3}\.){3}\d{1,3}$/.test(host);
        const targetPort = currentPort === "3001" ? "3000" : "3001";
        const adminUrl = isLocal ? `${window.location.protocol}//${host}:${targetPort}/dashboard` : `${window.location.protocol}//${host}/admin/dashboard`;
        console.log("[CustomerPanel] Admin route detected on Customer Panel. Redirecting to Admin Panel:", adminUrl);
        window.location.href = adminUrl;
      }
    }
  }, []);

  const getWouterBase = () => {
    if (typeof window !== "undefined") {
      const publicUrl = process.env.PUBLIC_URL || "";
      if (publicUrl && publicUrl !== "." && publicUrl.startsWith("/")) {
        const cleanBase = publicUrl.replace(/\/$/, "");
        if (window.location.pathname.startsWith(cleanBase)) {
          return cleanBase;
        }
      }
    }
    return "";
  };

  return (
    <ErrorBoundary>
      <div id="global-debug-error" style={{ display: "none" }} className="fixed bottom-4 right-4 bg-red-950 text-red-200 p-4 rounded-xl shadow-2xl z-[99999] border border-red-500 font-mono text-xs max-w-xl max-h-60 overflow-auto whitespace-pre-wrap" />
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <ImpersonationBanner />
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <WouterRouter base={getWouterBase()}>
              <Router />
            </WouterRouter>
            <Toaster />
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
